import {
  CircleBufferGeometry,
  DataTexture,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  RGBAFormat,
  Sprite,
  SpriteMaterial,
  TextureLoader,
} from 'three';
import CharacterImage from '../res/yellow-character-right.png';

// The sprite stays readable in the fixed camera; the artwork faces the next platform.
export default class YellowCharacter extends Group {
  constructor(height, onLoad) {
    super();
    this.name = 'yellow-character';
    this.baseHeight = height;
    this.onLoad = onLoad;
    this.skinImage = CharacterImage;
    const texture = new TextureLoader().load(CharacterImage, onLoad);
    const material = new SpriteMaterial({
      map: texture, transparent: true, alphaTest: .03, depthWrite: false,
    });
    this.portrait = new Sprite(material);
    this.portrait.name = 'character-portrait';
    this.facing = 1;
    this.portrait.scale.set(height * 1265 / 1244, height, 1);
    // The side-view artwork has a 59px bottom margin on a 1244px canvas. Compensate for the
    // fixed isometric camera's elevation so the visible feet touch the platform.
    this.portrait.position.y = height * (.5 - 59 / 1244) / Math.sqrt(2 / 3);
    this.add(this.portrait);

    const size = 32;
    const pixels = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const distance = Math.hypot((x + .5) / size * 2 - 1, (y + .5) / size * 2 - 1);
        pixels[(y * size + x) * 4 + 3] = Math.round(110 * Math.pow(Math.max(0, 1 - distance), 1.3));
      }
    }
    const shadowTexture = new DataTexture(pixels, size, size, RGBAFormat);
    shadowTexture.magFilter = LinearFilter;
    shadowTexture.needsUpdate = true;
    this.contactShadow = new Mesh(
      new CircleBufferGeometry(height * .24, 32),
      new MeshBasicMaterial({map: shadowTexture, transparent: true, depthWrite: false}),
    );
    this.contactShadow.name = 'contact-shadow';
    this.contactShadow.rotation.x = -Math.PI / 2;
    this.contactShadow.position.y = .04;
    this.add(this.contactShadow);
  }

  setJumpRotation(angle) {
    this.portrait.material.rotation = angle * this.facing;
  }

  setSkin(skin) {
    const image = skin && skin.image || CharacterImage;
    if (this.skinImage === image) return;
    this.skinImage = image;
    const old = this.portrait.material.map;
    const texture = new TextureLoader().load(image, this.onLoad);
    texture.repeat.x = this.facing;
    texture.offset.x = this.facing === 1 ? 0 : 1;
    this.portrait.material.map = texture;
    this.portrait.material.needsUpdate = true;
    old.dispose();
    const art = skin && skin.image ? skin : {width: 1265, height: 1244, bottom: 59};
    // Match visible height despite different transparent padding; never accumulate scaling.
    const height = this.baseHeight * (1120 / 1244) / ((art.visibleHeight || art.height * 1120 / 1244) / art.height);
    this.portrait.scale.y = height;
    this.portrait.scale.x = height * art.width / art.height;
    this.portrait.position.y = height * (.5 - art.bottom / art.height) / Math.sqrt(2 / 3);
  }

  faceTowards(dx, dz) {
    // Camera is at (-x, +y, +z), so both +x and +z project toward screen right.
    const horizontal = dx + dz;
    if (Math.abs(horizontal) < 1e-6) return;
    this.facing = horizontal > 0 ? 1 : -1;
    const texture = this.portrait.material.map;
    texture.repeat.x = this.facing;
    texture.offset.x = this.facing === 1 ? 0 : 1;
  }

  setAirborne(airborne) {
    this.contactShadow.visible = !airborne;
  }
}
